import { FC, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import styles from './verticalbarchart.module.sass';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';
import type { ChartTooltipRow } from '../ChartTooltip/ChartTooltip';

// ──────────────────────────────────────────────────────────────
// VERTICAL BAR CHART — polished columns with Y-axis, grid, trend
// Uses shared ChartTooltip (light glassmorphism).
// ──────────────────────────────────────────────────────────────

export interface VerticalBarChartItem {
  label: string;
  value: number;
  displayValue?: string;
  color?: string;
}

export interface VerticalBarGroup {
  label: string;
  items: Array<Omit<VerticalBarChartItem, 'label'> & { subLabel?: string }>;
  totalDisplayValue?: string;
}

export interface TrendLineConfig {
  values?: number[];
  color?: string;
  label?: string;
  /** Ring marker on every point. Default true; disable for dense series
   *  (30+ points) where the rings crowd the line. */
  showDots?: boolean;
  /** Opt-in tooltip row for the trend value: provide a formatter (and
   *  optionally `tooltipLabel` when the bottom legend `label` is unwanted).
   *  Charts that pass neither keep their tooltip unchanged. */
  formatValue?: (v: number) => string;
  tooltipLabel?: string;
}

export interface SecondaryLineConfig {
  values: number[];
  color: string;
  label: string;
  formatTick?: (v: number) => string;
  /** Index from which the line is drawn dashed, for the tail of a series whose
   *  last points are not yet settled. A rate read on a young cohort is not the
   *  same measurement as one read on a finished cohort, and a single unbroken
   *  line invites reading the difference between them as a trend. Marking it on
   *  the bars is not enough: the bars carry the population, the line carries the
   *  rate, and it is the rate that is provisional. */
  dashedFrom?: number;
}

export interface VerticalBarChartProps {
  items?: VerticalBarChartItem[];
  groups?: VerticalBarGroup[];
  height?: number;
  showAxis?: boolean;
  ticks?: number;
  formatTick?: (v: number) => string;
  trendLine?: TrendLineConfig;
  barLegendLabel?: string;
  barLegendItems?: Array<{ label: string; color: string }>;
  showBarLabels?: boolean;
  stacked?: boolean;
  secondaryLine?: SecondaryLineConfig;
  /** Several overlay lines sharing one right-hand axis. Only for lines that are
   *  the SAME quantity measured differently — otherwise they are two charts
   *  drawn on top of each other. Takes precedence over `secondaryLine`. */
  secondaryLines?: SecondaryLineConfig[];
  /** External hover index — when set, overrides the chart's internal hover.
   *  Use with `onHoverChange` to sync hover across multiple charts. */
  externalHoverIdx?: number | null;
  /** Notify parent of internal hover changes (for cross-chart sync). Fires
   *  with the hovered bar index, or null on leave. */
  onHoverChange?: (idx: number | null) => void;
  /** Whether an externally supplied index also opens the tooltip. True for
   *  cross-chart cursor sync, where the follower chart should show the same
   *  tooltip as the leader. False when the caller uses `externalHoverIdx` to
   *  mark a SELECTED bar rather than a hovered one: the tooltip is placed from
   *  the bar's rectangle at the moment the pointer enters it, so with no pointer
   *  involved it renders at the top-left corner of the viewport and stays
   *  there. Bar emphasis follows the external index either way. */
  tooltipOnExternalHover?: boolean;
  className?: string;
}

import { getChartPalette } from '@/lib/chartColors';

const FALLBACK_PALETTE = ['#0F2341', '#2563EB', '#93C5FD', '#06B6D4', '#1A3A5C'];

function niceTicks(maxVal: number, count: number): number[] {
  if (maxVal <= 0) return [0];
  const raw = maxVal / (count - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const nice = [1, 2, 2.5, 5, 10].find((n) => n * mag >= raw) ?? 10;
  const step = nice * mag;
  const ticks: number[] = [];
  for (let v = 0; ticks.length < count + 2; v += step) {
    ticks.push(Math.round(v * 1e6) / 1e6);
    // Must exceed maxVal so no bar overflows, then stop with margin
    if (v > maxVal) break;
  }
  // Safety: if top tick <= maxVal, add one more step
  if (ticks[ticks.length - 1] <= maxVal) {
    ticks.push(Math.round((ticks[ticks.length - 1] + step) * 1e6) / 1e6);
  }
  return ticks;
}

function fmtTickDefault(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e9) {
    const n = abs / 1e9;
    return `${sign}${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}B`;
  }
  if (abs >= 1e6) {
    const n = abs / 1e6;
    return `${sign}${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}M`;
  }
  if (abs >= 1e3) {
    const n = abs / 1e3;
    return `${sign}${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}K`;
  }
  if (abs === 0) return '0';
  return `${sign}${abs % 1 === 0 ? String(abs) : abs.toFixed(1)}`;
}

/** Comfortable width per bar — chart scrolls horizontally if this would be exceeded */
const TARGET_BAR_WIDTH = 28;

export const VerticalBarChart: FC<VerticalBarChartProps> = ({
  items,
  groups,
  height = 200,
  showAxis = true,
  ticks: tickCount = 5,
  formatTick,
  trendLine,
  barLegendLabel,
  barLegendItems,
  showBarLabels = false,
  stacked = false,
  secondaryLine,
  secondaryLines,
  externalHoverIdx,
  onHoverChange,
  tooltipOnExternalHover = true,
  className,
}) => {
  const PALETTE = useMemo(() => getChartPalette(), []);
  const isGrouped = !!groups?.length;
  const plotRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(0);
  const [internalHIdx, setInternalHIdx] = useState<number | null>(null);
  // External hover index (when provided) overrides internal — that's how
  // cross-chart cursor sync works: one chart leads, the others follow.
  const hIdx = externalHoverIdx !== undefined ? externalHoverIdx : internalHIdx;
  // Emphasis follows `hIdx`; the tooltip follows this, which the caller can
  // restrict to real pointer hovers.
  const tipIdx = tooltipOnExternalHover ? hIdx : internalHIdx;
  const setHIdx = useCallback((idx: number | null) => {
    setInternalHIdx(idx);
    onHoverChange?.(idx);
  }, [onHoverChange]);
  const [tipX, setTipX] = useState(0);
  const [tipY, setTipY] = useState(0);

  useEffect(() => {
    if (!plotRef.current) return;
    const measure = () => setPlotWidth(plotRef.current?.clientWidth || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(plotRef.current);
    return () => ro.disconnect();
  }, []);

  const rawMax = isGrouped
    ? stacked
      ? Math.max(...groups!.map((g) => g.items.reduce((s, i) => s + i.value, 0)), 1)
      : Math.max(...groups!.flatMap((g) => g.items.map((i) => i.value)), 1)
    : Math.max(...(items || []).map((i) => i.value), 1);

  const trendMax = trendLine?.values ? Math.max(...trendLine.values) : 0;
  const dataMax = Math.max(rawMax, trendMax);

  const yTicks = useMemo(() => niceTicks(dataMax, tickCount), [dataMax, tickCount]);
  const axisMax = yTicks[yTicks.length - 1] || dataMax;

  const fmt = formatTick ?? fmtTickDefault;

  // Overlay lines on their own scale (e.g. %). One or several, and several
  // only when they are the SAME quantity measured differently — they share an
  // axis, so two lines here are a comparison and not two charts in one.
  const secLines = useMemo(
    () => (secondaryLines?.length ? secondaryLines : secondaryLine ? [secondaryLine] : []),
    [secondaryLines, secondaryLine],
  );

  const secTicks = useMemo(() => {
    if (!secLines.length) return [];
    const secMax = Math.max(...secLines.flatMap((l) => l.values), 0.01);
    return niceTicks(secMax, tickCount);
  }, [secLines, tickCount]);
  const secAxisMax = secTicks[secTicks.length - 1] || 1;
  const secFmt = secLines[0]?.formatTick ?? fmtTickDefault;

  const trendCoords = useMemo(() => {
    if (!trendLine || isGrouped || !plotWidth) return null;
    const vals = trendLine.values ?? (items || []).map((i) => i.value);
    const n = vals.length;
    if (n < 2) return null;
    return vals.map((v, i) => ({
      x: ((i + 0.5) / n) * plotWidth,
      y: (1 - v / axisMax) * height,
    }));
  }, [trendLine, items, axisMax, isGrouped, plotWidth, height]);

  // Secondary line coords (uses secAxisMax, not axisMax), one set per line.
  const secLineCoords = useMemo(() => {
    if (!secLines.length || !plotWidth) return null;
    const n = isGrouped ? groups!.length : (items || []).length;
    if (n < 2) return null;
    return secLines.map((line) =>
      line.values.slice(0, n).map((v, i) => ({
        x: ((i + 0.5) / n) * plotWidth,
        y: (1 - v / secAxisMax) * height,
      })),
    );
  }, [secLines, plotWidth, height, secAxisMax, isGrouped, groups, items]);

  const trendColor = trendLine?.color ?? '#F59E0B';
  const hasLegendContent = !!(trendLine && (barLegendLabel || trendLine.label)) || !!barLegendItems?.length || secLines.length > 0;
  const showLegend = hasLegendContent;

  // ─── Horizontal scroll: enforce a minimum width so bars don't compress below readability ───
  const plotMinWidth = useMemo(() => {
    const n = isGrouped ? (groups?.length ?? 0) : (items?.length ?? 0);
    const groupFactor = isGrouped && !stacked ? ((groups?.[0]?.items?.length ?? 1)) : 1;
    return n * TARGET_BAR_WIDTH * groupFactor;
  }, [isGrouped, groups, items, stacked]);

  // ─── Hover interaction ────────────────────────────────────
  const itemCount = isGrouped ? groups!.length : (items || []).length;

  const onBarEnter = useCallback((idx: number, el: HTMLElement) => {
    setHIdx(idx);
    if (el) {
      const barRect = el.getBoundingClientRect();
      setTipX(barRect.left + barRect.width / 2);
      setTipY(barRect.top);
    }
  }, []);

  const onBarLeave = useCallback(() => setHIdx(null), []);

  // ─── Tooltip rows ─────────────────────────────────────────
  const tipRows: ChartTooltipRow[] = useMemo(() => {
    if (hIdx === null) return [];
    const rows: ChartTooltipRow[] = [];
    if (isGrouped && groups) {
      const g = groups[hIdx];
      if (!g) return [];
      g.items.forEach((item, ii) => {
        rows.push({
          color: item.color || PALETTE[ii % PALETTE.length],
          label: item.subLabel || g.label,
          value: item.displayValue || String(item.value),
        });
      });
      if (stacked && g.totalDisplayValue) {
        rows.push({ color: 'transparent', label: 'Total', value: g.totalDisplayValue });
      }
    } else if (items) {
      const item = items[hIdx];
      if (!item) return [];
      rows.push({
        color: item.color || PALETTE[hIdx % PALETTE.length],
        label: item.label,
        value: item.displayValue || String(item.value),
      });
    }
    if (trendLine && (trendLine.formatValue || trendLine.tooltipLabel)) {
      const vals = trendLine.values ?? (items || []).map((i) => i.value);
      if (hIdx < vals.length) {
        rows.push({
          color: trendLine.color || 'var(--color-text-primary)',
          label: trendLine.tooltipLabel ?? trendLine.label ?? 'Trend',
          value: (trendLine.formatValue ?? fmtTickDefault)(vals[hIdx]),
        });
      }
    }
    for (const line of secLines) {
      if (hIdx !== null && hIdx < line.values.length) {
        rows.push({
          color: line.color,
          label: line.label,
          value: (line.formatTick ?? fmtTickDefault)(line.values[hIdx]),
        });
      }
    }
    return rows;
  }, [hIdx, isGrouped, groups, items, stacked, secLines, trendLine]);

  const tipHeader = useMemo(() => {
    if (hIdx === null) return '';
    if (isGrouped && groups) return groups[hIdx]?.label || '';
    if (items) return items[hIdx]?.label || '';
    return '';
  }, [hIdx, isGrouped, groups, items]);

  return (
    <div ref={wrapRef} className={classNames(styles.wrap, className)}>
      <div className={styles.chartArea}>
        {showAxis && (
          <div className={styles.yAxis} style={{ height }}>
            {[...yTicks].reverse().map((v, i) => (
              <span key={i} className={styles.yTick}>{fmt(v)}</span>
            ))}
          </div>
        )}

        <div className={styles.scrollArea} ref={scrollRef}>
        <div className={styles.plotArea} ref={plotRef} style={{ minWidth: plotMinWidth }}>
          {showAxis && (
            <div className={styles.grid} style={{ height }}>
              {yTicks.map((_, i) => (
                <div key={i} className={styles.gridLine} />
              ))}
            </div>
          )}

          <div className={styles.bars} style={{ height }}>
            {isGrouped
              ? (stacked
                ? groups!.map((g, gi) => {
                    const sum = g.items.reduce((s, i) => s + i.value, 0);
                    return (
                      <div key={gi} className={styles.group}
                        onMouseEnter={(e) => onBarEnter(gi, e.currentTarget as HTMLElement)}
                        onMouseLeave={onBarLeave}
                      >
                        <div className={styles.stackedWrap} style={{ height }}>
                          <div
                            className={classNames(styles.bar, { [styles.barDim]: hIdx !== null && hIdx !== gi })}
                            style={{
                              height: `${(sum / axisMax) * 100}%`,
                              background: 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              overflow: 'hidden',
                            }}
                          >
                            {[...g.items].reverse().map((item, ii) => (
                              <div
                                key={ii}
                                className={styles.stackedSegment}
                                style={{
                                  height: sum > 0 ? `${(item.value / sum) * 100}%` : '0%',
                                  background: item.color || PALETTE[(g.items.length - 1 - ii) % PALETTE.length],
                                }}
                              />
                            ))}
                          </div>
                        </div>
                        <span className={styles.label}>{g.label}</span>
                      </div>
                    );
                  })
                : groups!.map((g, gi) => (
                  <div
                    key={gi}
                    className={styles.group}
                    onMouseEnter={(e) => onBarEnter(gi, e.currentTarget as HTMLElement)}
                    onMouseLeave={onBarLeave}
                  >
                    <div className={styles.groupBars} style={{ height }}>
                      {g.items.map((item, ii) => (
                        <div
                          key={ii}
                          className={classNames(styles.bar, { [styles.barDim]: hIdx !== null && hIdx !== gi })}
                          style={{
                            height: `${(item.value / axisMax) * 100}%`,
                            background: item.color || PALETTE[ii % PALETTE.length],
                          }}
                        />
                      ))}
                    </div>
                    <span className={styles.label}>{g.label}</span>
                  </div>
                )))
              : (items || []).map((item, idx) => {
                  const barPct = (item.value / axisMax) * 100;
                  const barWidth = itemCount > 0 ? plotWidth / itemCount : 0;
                  const labelFits = showBarLabels && item.displayValue && barWidth >= 32;
                  return (
                    <div
                      key={idx}
                      className={classNames(styles.bar, { [styles.barDim]: hIdx !== null && hIdx !== idx })}
                      style={{ height: `${barPct}%` }}
                      onMouseEnter={(e) => onBarEnter(idx, e.currentTarget as HTMLElement)}
                      onMouseLeave={onBarLeave}
                    >
                      <div
                        className={styles.barFill}
                        style={{ background: item.color || PALETTE[idx % PALETTE.length] }}
                      />
                      {labelFits && (
                        <span className={styles.barLabel}>
                          {item.displayValue}
                        </span>
                      )}
                    </div>
                  );
                })}
          </div>

          {trendCoords && plotWidth > 0 && (
            <svg
              className={styles.trendSvg}
              width={plotWidth}
              height={height}
            >
              <polyline
                className={styles.trendLine}
                points={trendCoords.map((d) => `${d.x},${d.y}`).join(' ')}
                style={{ stroke: trendColor }}
              />
              {trendLine?.showDots !== false &&
                trendCoords.map((d, i) => (
                  <circle
                    key={i}
                    className={styles.trendDot}
                    cx={d.x}
                    cy={d.y}
                    r={4}
                    style={{ stroke: trendColor }}
                  />
                ))}
            </svg>
          )}

          {/* Secondary line (own scale) */}
          {secLineCoords && plotWidth > 0 && (
            <svg
              className={styles.trendSvgSecondary}
              width={plotWidth}
              height={height}
            >
              {secLineCoords.map((coords, li) => {
                // `dashedFrom` splits the polyline in two. The solid part keeps
                // the draw-in animation; the provisional tail gets a real dash
                // pattern, which the animated class cannot carry because it
                // already uses stroke-dasharray to draw itself. The segments
                // overlap by one point so the join is continuous.
                const line = secLines[li];
                const cut = line.dashedFrom;
                const split = cut != null && cut > 0 && cut < coords.length ? cut : null;
                const solid = split == null ? coords : coords.slice(0, split);
                const provisional = split == null ? [] : coords.slice(split - 1);
                const pts = (ds: typeof coords) => ds.map((d) => `${d.x},${d.y}`).join(' ');
                return (
                  <g key={li}>
                    {solid.length > 1 && (
                      <polyline className={styles.trendLine} points={pts(solid)}
                        style={{ stroke: line.color }} />
                    )}
                    {provisional.length > 1 && (
                      <polyline className={styles.trendLineProvisional} points={pts(provisional)}
                        style={{ stroke: line.color }} />
                    )}
                    {coords.map((d, i) => (
                      <circle key={i}
                        className={split != null && i >= split ? styles.trendDotProvisional : styles.trendDot}
                        cx={d.x} cy={d.y} r={4}
                        style={{ stroke: line.color }} />
                    ))}
                  </g>
                );
              })}
            </svg>
          )}

          {/* Shared ChartTooltip, portal-rendered at viewport coords */}
          <ChartTooltip
            visible={tipIdx !== null}
            header={isGrouped ? tipHeader : undefined}
            rows={tipRows}
            x={tipX}
            y={tipY}
          />

          {!isGrouped && items && (
            <div className={styles.labels}>
              {items.map((item, idx) => {
                // Show label at every 4th position (months 4, 8, 12…) and always the last
                // Few bars (a ranked top-N): label every one. Long series (months): thin to every 4th.
                const show = items.length <= 12 || (idx + 1) % 4 === 0 || idx === items.length - 1;
                return (
                  <span
                    key={idx}
                    className={classNames(styles.label, !show && 'invisible')}
                  >
                    {item.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        </div>{/* end scrollArea */}

        {/* Right Y-axis for secondary line */}
        {secLines.length > 0 && showAxis && (
          <div className={styles.yAxisRight} style={{ height }}>
            {[...secTicks].reverse().map((v, i) => (
              <span key={i} className={styles.yTick}>{secFmt(v)}</span>
            ))}
          </div>
        )}
      </div>

      {showLegend && (
        <div className={styles.legend}>
          {barLegendItems ? (
            barLegendItems.map((item, i) => (
              <div key={i} className={styles.legendItem}>
                <div className={styles.legendDot} style={{ background: item.color }} />
                <span className={styles.legendText}>{item.label}</span>
              </div>
            ))
          ) : barLegendLabel ? (
            <div className={styles.legendItem}>
              <div className={styles.legendDot} style={{ background: PALETTE[0] }} />
              <span className={styles.legendText}>{barLegendLabel}</span>
            </div>
          ) : null}
          {trendLine?.label && (
            <div className={styles.legendItem}>
              <div className={styles.legendLine} style={{ background: trendColor }} />
              <span className={styles.legendText}>{trendLine.label}</span>
            </div>
          )}
          {secLines.map((line, i) => (
            <div key={i} className={styles.legendItem}>
              <div className={styles.legendLine} style={{ background: line.color }} />
              <span className={styles.legendText}>{line.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

VerticalBarChart.displayName = 'VerticalBarChart';

export default VerticalBarChart;
